import I18n from 'I18n';
import unescapedText from 'I18n/utils/unescapedText';
import { useSearchParams } from 'marketplace-ui-common/hooks/useSearchParams';
import { interpolate } from 'marketplace-ui-common/i18n/utils';
import { useMemo } from 'react';
import { sanitize } from 'sanitize-text/sanitizers/Sanitize';
import { useAppliedFilters } from '../../filters/hooks/useAppliedFilters';
import { useFilterConfig } from '../../filters/hooks/useFilterConfig';
import { useProductTypeSearchParam } from '../../hooks/useProductTypeSearchParams';
import { useCollectionQuery } from './useCollectionQuery';
import { useSearchResultsQuery } from './useSearchResultsQuery';
export function useSearchResultsPageMetadata() {
  var _data$result$total, _filterConfig$AgentTe2, _filterConfig$Categor2;
  const collectionResult = useCollectionQuery();
  const productType = useProductTypeSearchParam();
  const searchParams = useSearchParams();
  const appliedFilters = useAppliedFilters();
  const filterConfig = useFilterConfig();
  const {
    data
  } = useSearchResultsQuery();
  const searchTerm = searchParams.get('search');
  const count = (_data$result$total = data === null || data === void 0 ? void 0 : data.result.total) !== null && _data$result$total !== void 0 ? _data$result$total : 0;
  const translationCount = count > 0 ? count : 1;
  const formattedCount = I18n.formatNumber(count);
  return useMemo(() => {
    if (collectionResult.data && !collectionResult.isUmbrellaCollection) {
      return {
        title: interpolate(collectionResult.data.publishedListing.htmlPageTitle, {
          count: formattedCount
        }),
        heading: interpolate(collectionResult.data.publishedListing.title, {
          count: formattedCount
        }),
        metaDescription: collectionResult.data.publishedListing.metaDescription
      };
    }
    if (searchTerm) {
      return {
        title: unescapedText('marketplaceStorefront.documentHead.searchResults.defaultWithSearch.title', {
          searchTerm: sanitize(searchTerm)
        }),
        heading: unescapedText('marketplaceStorefront.headers.searchResults'),
        metaDescription: unescapedText('marketplaceStorefront.documentHead.searchResults.defaultWithSearch.metaDescription')
      };
    }

    // App product type handling
    if (productType === 'APP') {
      var _categoryFilter$value, _filterConfig$Categor;
      // Get category filter for apps
      const categoryFilter = appliedFilters.find(f => f.field === 'CATEGORY');
      const singleCategory = (categoryFilter === null || categoryFilter === void 0 || (_categoryFilter$value = categoryFilter.values) === null || _categoryFilter$value === void 0 ? void 0 : _categoryFilter$value.length) === 1 ? categoryFilter.values[0] : null;

      // App with single category
      const categoryOption = (_filterConfig$Categor = filterConfig.Category) === null || _filterConfig$Categor === void 0 || (_filterConfig$Categor = _filterConfig$Categor.options) === null || _filterConfig$Categor === void 0 ? void 0 : _filterConfig$Categor.find(opt => singleCategory && opt.value === singleCategory);

      // App without category or multiple categories
      if (!categoryOption) {
        return {
          title: unescapedText('marketplaceStorefront.documentHead.searchResults.apps.title', {
            count: translationCount,
            formattedCount
          }),
          heading: unescapedText('marketplaceStorefront.searchResults.header.allApps'),
          metaDescription: unescapedText('marketplaceStorefront.documentHead.searchResults.apps.metaDescription')
        };
      }
      const categoryName = categoryOption.text;
      return {
        title: unescapedText('marketplaceStorefront.documentHead.searchResults.apps.categoryTitle', {
          count: translationCount,
          formattedCount,
          category: categoryName
        }),
        heading: unescapedText('marketplaceStorefront.documentHead.searchResults.apps.categoryHeading', {
          category: categoryName
        }),
        metaDescription: unescapedText('marketplaceStorefront.documentHead.searchResults.apps.categoryMetaDescription', {
          category: categoryName
        })
      };
    }

    // Agent product type handling
    if (productType === 'AGENT') {
      var _teamFilter$values, _filterConfig$AgentTe;
      // Get team filter for agents
      const teamFilter = appliedFilters.find(f => f.field === 'AGENT_TEAM');
      const singleTeam = (teamFilter === null || teamFilter === void 0 || (_teamFilter$values = teamFilter.values) === null || _teamFilter$values === void 0 ? void 0 : _teamFilter$values.length) === 1 ? teamFilter.values[0] : null;

      // Agent with single team
      const teamOption = (_filterConfig$AgentTe = filterConfig.AgentTeam) === null || _filterConfig$AgentTe === void 0 || (_filterConfig$AgentTe = _filterConfig$AgentTe.options) === null || _filterConfig$AgentTe === void 0 ? void 0 : _filterConfig$AgentTe.find(opt => singleTeam && opt.value === singleTeam);

      // Agent without team or multiple teams
      if (!teamOption) {
        return {
          title: unescapedText('marketplaceStorefront.documentHead.searchResults.agents.title', {
            count: translationCount,
            formattedCount
          }),
          heading: unescapedText('marketplaceStorefront.searchResults.header.allAgents'),
          metaDescription: unescapedText('marketplaceStorefront.documentHead.searchResults.agents.metaDescription')
        };
      }
      const teamName = (teamOption === null || teamOption === void 0 ? void 0 : teamOption.text) || singleTeam;
      return {
        title: unescapedText('marketplaceStorefront.documentHead.searchResults.agents.teamTitle', {
          count: translationCount,
          formattedCount,
          team: teamName
        }),
        heading: unescapedText('marketplaceStorefront.documentHead.searchResults.agents.teamHeading', {
          team: teamName
        }),
        metaDescription: unescapedText('marketplaceStorefront.documentHead.searchResults.agents.teamMetaDescription', {
          team: teamName
        })
      };
    }
    return {
      title: unescapedText('marketplaceStorefront.documentHead.searchResults.default.title', {
        count: translationCount,
        formattedCount
      }),
      heading: unescapedText('marketplaceStorefront.searchResults.header.allProducts'),
      metaDescription: unescapedText('marketplaceStorefront.documentHead.searchResults.default.metaDescription')
    };
  }, [appliedFilters, collectionResult.data, collectionResult.isUmbrellaCollection, (_filterConfig$AgentTe2 = filterConfig.AgentTeam) === null || _filterConfig$AgentTe2 === void 0 ? void 0 : _filterConfig$AgentTe2.options, (_filterConfig$Categor2 = filterConfig.Category) === null || _filterConfig$Categor2 === void 0 ? void 0 : _filterConfig$Categor2.options, formattedCount, productType, searchTerm, translationCount]);
}